<script lang="ts">
import ElTooltip, { elTooltipPositions } from '@/ElTooltip.vue';
export type ElTruncateTextPosition = (typeof elTooltipPositions)[number];
</script>

<script setup lang="ts">
/**
 * ElTruncateText
 *
 * A component that automatically truncates text with ellipsis when it overflows its container
 * and shows a tooltip with the full text when the user hovers over it.
 *
 * Features:
 * - Automatically detects if text is truncated using scrollWidth vs clientWidth comparison
 * - Responsive: Uses ResizeObserver to re-check truncation when container size changes
 * - Tooltip appears when hovering over the truncated text
 * - Only shows tooltip when text is actually truncated
 * - Configurable tooltip position, delay, and mobile visibility
 * - Optional mobile-specific behavior (disable truncation on mobile)
 *
 * @example
 * // Default behavior - tooltip on hover over text
 * <ElTruncateText text="This is a very long text that will be truncated" />
 *
 * @example
 * // Disable truncation on mobile - show full text
 * <ElTruncateText text="Long text" :disable-truncation-on-mobile="true" />
 */
import { ref, computed, watch, nextTick } from 'vue';
import { useResizeObserver } from '@vueuse/core';

const props = withDefaults(
  defineProps<{
    /** Text to display. Will be truncated with ellipsis if it overflows the container. */
    text: string;

    /**
     * Position of the tooltip relative to the text.
     * @default 'top'
     */
    position?: ElTruncateTextPosition;

    /**
     * Delay time before the tooltip appears (in milliseconds).
     * @default 200
     */
    delayTime?: string | number;

    /**
     * Hide tooltip on mobile devices.
     * @default false
     */
    hideTooltipOnMobile?: boolean;

    /**
     * Disable truncation on mobile devices. When true, text will wrap normally instead of truncating.
     * @default false
     */
    disableTruncationOnMobile?: boolean;

    /**
     * Additional CSS class for the text container.
     * @default ''
     */
    customClass?: string;
  }>(),
  {
    position: 'top',
    delayTime: 200,
    hideTooltipOnMobile: false,
    disableTruncationOnMobile: false,
    customClass: '',
  },
);

const textElement = ref<HTMLElement>();
const isTruncated = ref(false);

const checkTruncation = () => {
  if (textElement.value) {
    isTruncated.value = textElement.value.scrollWidth > textElement.value.clientWidth;
  }
};

useResizeObserver(textElement, () => {
  checkTruncation();
});

watch(
  () => props.text,
  async () => {
    // Use nextTick to ensure the DOM is updated
    await nextTick();
    checkTruncation();
  },
  { immediate: true },
);

const tooltipTitle = computed(() => (isTruncated.value ? props.text : ''));
</script>

<template>
  <!-- Simple text on mobile when disableTruncationOnMobile is true -->
  <span v-if="disableTruncationOnMobile" class="inline-block max-w-full md:hidden" :class="customClass">
    {{ text }}
  </span>

  <!-- Full truncation logic for desktop (or always if disableTruncationOnMobile is false) -->
  <span :class="disableTruncationOnMobile ? 'hidden md:inline-block md:max-w-full' : 'inline-block max-w-full'">
    <ElTooltip v-if="isTruncated" :title="tooltipTitle" :position="position" :delay-time="delayTime" :hide-on-mobile="hideTooltipOnMobile">
      <span ref="textElement" class="inline-block truncate max-w-full" :class="customClass">
        {{ text }}
      </span>
    </ElTooltip>

    <span v-else ref="textElement" class="inline-block truncate max-w-full" :class="customClass">
      {{ text }}
    </span>
  </span>
</template>
